import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiKey, FiArrowRight, FiCheckCircle, FiShield,
  FiSettings, FiUsers
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const AdminPermissions = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/admin/permissions" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/admin" className="hover:text-blue-600">Admin & Permissions</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Permission System</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl mb-4">
              <FiKey className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Permission System</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Understand how permissions work and configure access controls for your organization.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">How Permissions Work</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Tasklina uses a role-based access control (RBAC) system where permissions are granted 
                through roles. Each role has a predefined set of permissions that determine what actions 
                users can perform.
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Permission Hierarchy</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Higher-level roles inherit permissions from lower-level roles. For example, an 
                  Organization Admin has all permissions of a Department Manager plus additional ones.
                </p>
              </div>
            </div>
          </section>

          {/* Permission Categories */}
          <section className="mb-16" id="categories">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Permission Categories</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiUsers className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">User Management</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-8">
                  <li>• Invite users to organization</li>
                  <li>• Assign and modify roles</li>
                  <li>• Deactivate user accounts</li>
                </ul>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiSettings className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Organization Settings</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-8">
                  <li>• Manage organization profile</li>
                  <li>• Configure billing settings</li>
                  <li>• Create departments and teams</li>
                </ul>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiShield className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Security Settings</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-8">
                  <li>• Configure security policies</li>
                  <li>• View audit logs</li>
                  <li>• Manage SSO settings</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Permission by Role */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Permissions by Role</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b">Permission</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-b">Org Admin</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-b">Department Manager</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-b">Team Lead</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-b">Employee</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 border-b">Invite Users</td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 border-b">Manage Departments</td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 border-b">Manage Teams</td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 border-b">Create Projects</td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 border-b">Assign Tasks</td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><FiCheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center border-b"><span className="text-gray-300">—</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/admin/roles"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Role Hierarchy</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/security"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Security Settings</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default AdminPermissions;
